import { useCallback, useEffect, useState } from 'react'

import { api, ApiError, type ImageSummary, type Profile } from '../api'
import { DeckImage } from '../components/DeckImage'
import { ProblemBanner } from '../components/Problem'

const MAX_BYTES = 5 * 1024 * 1024

/** Upload goes straight to S3 with a presigned POST; the API never handles the bytes. */
export default function Images() {
  const [images, setImages] = useState<ImageSummary[]>([])
  const [profile, setProfile] = useState<Profile | null>(null)
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [unusedOnly, setUnusedOnly] = useState(false)

  const load = useCallback(() => {
    api
      .listImages()
      .then((result) => setImages(result.images))
      .catch(setError)
    // Usage is shown here so a delete visibly frees space against the caps.
    api
      .me()
      .then(setProfile)
      .catch(() => setProfile(null))
  }, [])

  useEffect(load, [load])

  const upload = async () => {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const presigned = await api.requestUpload({
        name,
        contentType: file.type,
        sizeBytes: file.size,
      })
      const form = new FormData()
      Object.entries(presigned.fields).forEach(([key, value]) => form.append(key, value))
      form.append('file', file)
      const response = await fetch(presigned.url, {
        method: 'POST',
        body: form,
      })
      if (!response.ok) throw new Error('Upload was rejected by storage.')
      setName('')
      setFile(null)
      // Processing is asynchronous; the record appears as PENDING and turns READY.
      setTimeout(load, 1500)
    } catch (uploadError) {
      setError(uploadError)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Deletes each image through the same endpoint the MCP tool uses. The server refuses an
   * image a deck still references (409), so a failure is reported per image and does not
   * stop the rest of the batch.
   */
  const removeImages = async (names: string[]) => {
    setBusy(true)
    setError(null)
    setNotice(null)
    const failures: string[] = []
    for (const imageName of names) {
      try {
        await api.deleteImage(imageName)
      } catch (deleteError) {
        const reason =
          deleteError instanceof ApiError
            ? deleteError.problem.detail || deleteError.problem.title
            : 'could not be deleted'
        failures.push(`${imageName}: ${reason}`)
      }
    }
    const deleted = names.length - failures.length
    setSelected(new Set())
    if (failures.length > 0) {
      setError(
        new Error(`${failures.length} of ${names.length} not deleted. ${failures.join(' ')}`),
      )
    }
    if (deleted > 0) setNotice(`Deleted ${deleted} ${deleted === 1 ? 'image' : 'images'}.`)
    load()
    setBusy(false)
  }

  const deleteOne = (imageName: string) => {
    if (!window.confirm(`Delete “${imageName}”? This cannot be undone.`)) return
    void removeImages([imageName])
  }

  const deleteSelected = () => {
    const names = [...selected]
    if (names.length === 0) return
    if (!window.confirm(`Delete ${names.length} selected image(s)? This cannot be undone.`)) {
      return
    }
    void removeImages(names)
  }

  const toggle = (imageName: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(imageName)) next.delete(imageName)
      else next.add(imageName)
      return next
    })
  }

  const unused = images.filter((image) => image.usedBy.length === 0)
  const shown = unusedOnly ? unused : images

  const deleteUnused = () => {
    if (unused.length === 0) return
    if (!window.confirm(`Delete ${unused.length} unused image(s)? This cannot be undone.`)) return
    void removeImages(unused.map((image) => image.name))
  }

  const valid = /^[a-z0-9][a-z0-9-]{0,31}(\/[a-z0-9][a-z0-9-]{0,63}){1,2}$/.test(name)

  return (
    <>
      <h1>Images</h1>
      <ProblemBanner error={error} />
      {notice ? (
        <p className="muted" role="status">
          {notice}
        </p>
      ) : null}
      {profile ? (
        <p className="muted" data-testid="image-usage">
          {profile.usage.images} of {profile.usage.imageLimit} images,{' '}
          {Math.round(profile.usage.imageBytes / (1024 * 1024))} of{' '}
          {Math.round(profile.usage.imageByteLimit / (1024 * 1024))} MB used.
        </p>
      ) : null}

      <section className="card">
        <h2>Upload</h2>
        <div className="row">
          <input
            value={name}
            placeholder="bfm/turn-circle"
            aria-label="Image name"
            onChange={(event) => setName(event.target.value)}
          />
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <button
            className="primary"
            disabled={busy || !valid || !file || (file?.size ?? 0) > MAX_BYTES}
            onClick={upload}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        <p className="muted">
          Lowercase letters, digits and hyphens, in two or three segments. PNG, JPEG or WebP, up to
          5 MB. Images are resized and stripped of metadata after upload.
        </p>
      </section>

      {images.length > 0 ? (
        <div className="row">
          <button
            className="danger"
            disabled={busy || selected.size === 0}
            onClick={deleteSelected}
          >
            Delete selected ({selected.size})
          </button>
          <button className="danger" disabled={busy || unused.length === 0} onClick={deleteUnused}>
            Delete {unused.length} unused {unused.length === 1 ? 'image' : 'images'}
          </button>
          <label className="muted">
            <input
              type="checkbox"
              checked={unusedOnly}
              onChange={(event) => setUnusedOnly(event.target.checked)}
            />{' '}
            Show unused only
          </label>
          <p className="muted">
            An image that a deck still uses cannot be deleted. Remove it from the deck first. A
            deck archived for inactivity does not hold its images, but restoring it later needs
            them to exist.
          </p>
        </div>
      ) : null}

      <div className="grid">
        {shown.map((image) => (
          <article className="card" key={image.name}>
            <h2>{image.name}</h2>
            <label className="muted">
              <input
                type="checkbox"
                checked={selected.has(image.name)}
                disabled={busy}
                onChange={() => toggle(image.name)}
              />{' '}
              Select for bulk delete
            </label>
            {image.status === 'READY' ? (
              <DeckImage name={image.name} variant="thumb" />
            ) : (
              <p className="muted">{image.status.toLowerCase()}</p>
            )}
            <p className="muted">{Math.round(image.derivedBytes / 1024)} KB</p>
            <p className="muted" data-testid="used-by">
              {image.usedBy.length > 0
                ? `Used by ${image.usedBy.map((deck) => deck.title).join(', ')}`
                : 'Not used by any deck'}
            </p>
            <button className="danger" disabled={busy} onClick={() => deleteOne(image.name)}>
              Delete
            </button>
          </article>
        ))}
      </div>
    </>
  )
}
